import React, {useState} from 'react';
import {Card, CardBody} from "reactstrap";
import appUserRoutes from "../../../appUserRoutes";
import useDidMountEffect from "../../../utils/useDidMountEffect";
import apiUser from "../../../apiUser";
import useAfterUpdateEffect from "../../../utils/useAfterUpdateEffect";
import {Link, Route, Switch} from "react-router-dom";
import {TecRmiProvider} from "../../../contexts/TecRmiContext";
import {Breadcrumbs, BreadcrumbsItem} from "react-breadcrumbs-dynamic";
import {ThroughProvider} from "react-through";
import {isNil} from "lodash";
import history from "../../../utils/history";
import TypeIdSearch from "../../../components/TecRmi/TypeIdSearch";
import TypeIdView from "./TypeIdView";
import LoadingSpinner from "../../../components/LoadingSpinner/LoadingSpinner";
import apiPlatform from "../../../apiPlatform";
import {TecRmiTypeDetailsProvider, useTecRmiTypeDetails} from "../../../contexts/TecRmiTypeDetailsContext";
import tecRmiTypeDetailsParse from "../../../utils/tecRmi/tecRmiTypeDetailsParse";
import showTypeToParamName from "../../../utils/tecRmi/showTypeToParamName";
import {TCD_VEHICLE_TYPE_CAR} from "../../../components/TecRmi/TecRmiVehicleTypeSuppliers";

export const TAB_PRIMARY = 'podstawowe'
export const TAB_MODULES = 'moduly'
export const TAB_COMPONENTS = 'podzespoly'
export const TAB_SERVICE_PROPOSAL = 'prace-przegladowe'
export const TAB_TYRES = 'opony'
export const TAB_TIPS = 'wskazowki'

const MainView = props => {
  const typeDetailsProvider = useTecRmiTypeDetails()
  const [ident, setIdent] = useState(props.match.params.ident ?? null)
  const [loading, setLoading] = useState(true)
  const [typeId, setTypeId] = useState(null)
  const [showType, setShowType] = useState()
  const [typeDetails, setTypeDetails] = useState(null)
  const [vehicle, setVehicle] = useState(null)
  const [repairOrder, setRepairOrder] = useState(null)

  useAfterUpdateEffect(() => {
    if(vehicle?.tecRmi?.typeId){
      setShowType(vehicle?.tecRmi?.tcdVehicleType ?? TCD_VEHICLE_TYPE_CAR)
      setTypeId(vehicle?.tecRmi?.typeId)
    }else{
      setLoading(false)
    }
  }, [vehicle])

  useAfterUpdateEffect(() => {
    if(repairOrder?.tecRmi?.typeId){
      setShowType(repairOrder?.tecRmi?.tcdVehicleType ?? TCD_VEHICLE_TYPE_CAR)
      setTypeId(repairOrder?.tecRmi?.typeId)
    }else{
      setLoading(false)
    }
  }, [repairOrder])

  useDidMountEffect(() => {
    if(!isNil(ident)){
      if(isVehicleIdIdent()){
        const vehicleId = ident.replace('v', '')
        apiUser.vehicles.get(vehicleId)
          .then(setVehicle)
      }else if(isRepairOrderIdIdent()){
        const repairOrderId = ident.replace('ro', '')
        apiUser.repairOrders.get(repairOrderId)
          .then(res => setRepairOrder(res.data))
      }else{
        const [showType, typeId] = ident.split('-')
        setShowType(showType)
        setTypeId(parseInt(typeId))
      }
    }else{
      setLoading(false)
    }
  })

  useAfterUpdateEffect(() => {
    fetchDetails()
  }, [typeId])

  const isVehicleIdIdent = () => ident?.indexOf('v') === 0
  const isRepairOrderIdIdent = () => ident?.indexOf('ro') === 0

  const fetchDetails = () => {
    setLoading(true)
    setTypeDetails(null)
    apiUser.tecRmi.vehicleTree.typeDetails({
      [showTypeToParamName(showType)]: true,
      typeId,
    })
      .then(res => {
        setTypeDetails(res.data[0])
        typeDetailsProvider.setTypeDetails(tecRmiTypeDetailsParse(res.data[0]))
      })
      .catch(() => {
        setTypeId(null)
        setShowType(null)
        setTypeDetails(null)
      })
      .finally(() => setLoading(false))
  }

  const handleOnTypeIdSearched = (typeId, showType) => {
    setTypeId(typeId)
    setShowType(showType)

    if(!isVehicleIdIdent() && !isRepairOrderIdIdent()){
      history.push(appUserRoutes.tecRmi.ident(`${showType}-${typeId}`))
    }
  }

  const handleOnTypeDetailsFetch = (typeDetails) => {
    if(isVehicleIdIdent()){
      apiPlatform.vehicles.update({
        id: vehicle.id,
        tecRmi: typeDetails
      })
    }else if (isRepairOrderIdIdent()) {
      apiPlatform.repairOrders.updateTecRmiData(repairOrder.id, typeDetails)
    }
  }

  const getIdentBreadcrumbLabel = () => {
    const values = []

    if(vehicle){
      if(vehicle.registrationNumber){
        values.push(vehicle.registrationNumber)
      }
      if(vehicle.vin){
        values.push(vehicle.vin)
      }
    }

    if(repairOrder){
      values.push(repairOrder.number)
    }

    if(typeDetails){
      const text = [typeDetails?.MakeName, typeDetails?.RangeName, typeDetails?.TypeName].join(' ')
      if(values.length === 0){
        values.push(text)
      }else{
        values.push(` (${text})`)
      }
    }

    if(values.length === 0 && !isVehicleIdIdent() && !isRepairOrderIdIdent()){
      return null
    }

    return values.join(' ')
  }

  return <ThroughProvider>
    <React.Fragment>
      <BreadcrumbsItem to={appUserRoutes.home}><i className="fas fa-home" /></BreadcrumbsItem>
      <BreadcrumbsItem to={appUserRoutes.tecRmi.main}>Dane naprawcze</BreadcrumbsItem>
      {getIdentBreadcrumbLabel() && <BreadcrumbsItem to={appUserRoutes.tecRmi.ident(ident)}>{getIdentBreadcrumbLabel()}</BreadcrumbsItem>}

      <div className="view-header">
        <Breadcrumbs
          container={'ol'}
          containerProps={{
            className: 'breadcrumb mb-0 bg-transparent border-0 px-0 d-flex flex-row flex-nowrap align-items-center'
          }}
          item={item => <li className="breadcrumb-item d-flex flex-row align-items-center flex-nowrap text-nowrap">
            <Link to={item.to}>{item.children}</Link>
          </li>}
          finalItem={item => <li className="breadcrumb-item d-flex flex-row align-items-center flex-nowrap text-nowrap active">{item.children}</li>}
        />
      </div>

      <Card>
        <CardBody>
          {
            loading
              ? <LoadingSpinner />
              : <React.Fragment>
                {!typeId && <TypeIdSearch
                  vin={vehicle?.vin ?? undefined}
                  makeName={!vehicle?.vin ? vehicle?.brand : undefined}
                  rangeName={!vehicle?.vin ? vehicle?.model : undefined}
                  onTypeIdChange={handleOnTypeIdSearched}
                  onTypeDetailsFetch={handleOnTypeDetailsFetch}
                />}
                {typeId && <TecRmiProvider typeId={typeId} ident={ident} showType={showType}>
                  <Switch>
                    <Route path={appUserRoutes.tecRmi.ident(ident)} component={TypeIdView}/>
                  </Switch>
                </TecRmiProvider>}
              </React.Fragment>
          }
        </CardBody>
      </Card>
    </React.Fragment>
  </ThroughProvider>
};

const MainViewWrapper = (props) => {
  return <TecRmiTypeDetailsProvider>
    <MainView {...props} />
  </TecRmiTypeDetailsProvider>
}

MainViewWrapper.propTypes = {

};

export default MainViewWrapper;